import { render, type ComponentType, type VNode } from 'preact'
import { baseStyles } from './styles'

export interface WidgetProps {
  /** The custom element itself, for widgets that need to dispatch events. */
  host: HTMLElement
  /**
   * Observed attributes arrive as camelCase props. The index signature is what
   * lets a widget declare only the attributes it cares about and still satisfy
   * this contract.
   */
  [attribute: string]: string | HTMLElement | undefined
}

export interface WidgetDefinition<P extends WidgetProps = WidgetProps> {
  /** Custom element name. Must contain a dash, per the spec. */
  tag: string
  component: ComponentType<P>
  /**
   * Attributes to observe, in kebab-case. They reach the component as
   * camelCase props — `api-base` becomes `apiBase`.
   */
  attributes?: readonly string[]
  /** Widget-specific CSS appended after the shared base sheet. */
  styles?: string
}

const camelize = (name: string): string =>
  name.replace(/-([a-z])/g, (_, letter: string) => letter.toUpperCase())

/**
 * Constructable stylesheets are shared across every instance of a widget, so a
 * page with fifty score cards parses the CSS once. Browsers without
 * `adoptedStyleSheets` fall back to a per-root `<style>` element.
 */
function styleSheetFor(css: string): CSSStyleSheet | null {
  if (typeof CSSStyleSheet === 'undefined' || !('replaceSync' in CSSStyleSheet.prototype)) {
    return null
  }
  try {
    const sheet = new CSSStyleSheet()
    sheet.replaceSync(css)
    return sheet
  } catch {
    return null
  }
}

/**
 * Register one widget as a custom element rendering into a shadow root.
 *
 * The shadow root is what makes these safe to drop onto a stranger's site: the
 * host page's CSS cannot reach in and our CSS cannot leak out, so a widget
 * looks the same on every site that embeds it.
 */
export function defineWidget<P extends WidgetProps = WidgetProps>(
  definition: WidgetDefinition<P>,
): void {
  const { tag, component: Component, attributes = [], styles = '' } = definition

  // Re-registering a tag throws, and the bundle may legitimately be included
  // twice on a page that embeds two different publishers' snippets.
  if (customElements.get(tag)) return

  const css = `${baseStyles}\n${styles}`
  const sheet = styleSheetFor(css)

  class SportsWidgetElement extends HTMLElement {
    static readonly observedAttributes = attributes

    #root: ShadowRoot
    #connected = false

    constructor() {
      super()
      this.#root = this.attachShadow({ mode: 'open' })
      if (sheet) {
        this.#root.adoptedStyleSheets = [sheet]
      } else {
        const style = document.createElement('style')
        style.textContent = css
        this.#root.appendChild(style)
      }
    }

    connectedCallback(): void {
      this.#connected = true
      this.#render()
    }

    disconnectedCallback(): void {
      this.#connected = false
      // Unmount so the component's effects can abort in-flight requests and
      // clear their polling timers; a detached widget must not keep fetching.
      render(null, this.#root)
    }

    attributeChangedCallback(): void {
      if (this.#connected) this.#render()
    }

    #render(): void {
      const props = { host: this } as Record<string, unknown>
      for (const name of attributes) {
        const value = this.getAttribute(name)
        props[camelize(name)] = value === null ? undefined : value
      }
      render(<Component {...(props as P)} /> as VNode, this.#root)
    }
  }

  customElements.define(tag, SportsWidgetElement)
}
